import React, { useEffect, useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, CopyIcon, PlayIcon, SaveIcon, DownloadIcon, UploadIcon, ZoomInIcon, ZoomOutIcon, RotateCwIcon, CheckCircleIcon, XCircleIcon, ClockIcon, FileTextIcon, LayersIcon, BoxIcon, ArrowRightIcon, UserIcon, UsersIcon, BellIcon, AlertCircleIcon, ToggleLeftIcon, ToggleRightIcon, AlertTriangleIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import type { OrgUnit } from '../sharedFinancialData';
// Mock data types
type WorkflowDefinition = {
  id: string;
  name: string;
  description: string;
  documentType: string;
  version: string;
  status: 'Active' | 'Draft' | 'Inactive';
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  bpmnXml: string;
  createdAt: string;
  updatedAt: string;
  lastPublishedAt?: string;
  isDefault: boolean;
};
type DocumentType = {
  id: string;
  name: string;
  description: string;
  category: string;
};
export function WorkflowManager({
  locale
}) {
  const [activeView, setActiveView] = useState<'list' | 'edit' | 'preview'>('list');
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDefinition | null>(null);
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDocumentType, setFilterDocumentType] = useState<string>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Mock data for document types
  const documentTypes: DocumentType[] = [{
    id: 'invoice',
    name: 'Invoice',
    description: 'Accounts payable invoice',
    category: 'AP'
  }, {
    id: 'po',
    name: 'Purchase Order',
    description: 'Procurement purchase order',
    category: 'Procurement'
  }, {
    id: 'requisition',
    name: 'Requisition',
    description: 'Purchase requisition',
    category: 'Procurement'
  }, {
    id: 'vendor',
    name: 'Vendor',
    description: 'Vendor record',
    category: 'Vendor Management'
  }, {
    id: 'credit-note',
    name: 'Credit Note',
    description: 'AP credit note',
    category: 'AP'
  }, {
    id: 'receipt',
    name: 'Goods Receipt',
    description: 'Goods receipt document',
    category: 'Procurement'
  }, {
    id: 'expense',
    name: 'Expense Claim',
    description: 'Employee expense claim',
    category: 'Expenses'
  }, {
    id: 'contract',
    name: 'Contract',
    description: 'Vendor contract',
    category: 'Contract Management'
  }];
  // Mock data for workflows
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([{
    id: 'wf-001',
    name: 'Invoice Approval Workflow',
    description: 'Standard invoice approval process with delegation thresholds',
    documentType: 'invoice',
    version: '1.2',
    status: 'Active',
    scope: 'Global',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-01-15T10:00:00Z',
    updatedAt: '2023-03-10T14:30:00Z',
    lastPublishedAt: '2023-03-10T14:30:00Z',
    isDefault: true
  }, {
    id: 'wf-002',
    name: 'Purchase Order Approval',
    description: 'Approval workflow for purchase orders with budget validation',
    documentType: 'po',
    version: '1.0',
    status: 'Active',
    scope: 'Global',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-02-01T09:15:00Z',
    updatedAt: '2023-02-15T11:20:00Z',
    lastPublishedAt: '2023-02-15T11:20:00Z',
    isDefault: true
  }, {
    id: 'wf-003',
    name: 'Singapore High-Value Invoice',
    description: 'Special approval process for high-value invoices in Singapore',
    documentType: 'invoice',
    version: '1.0',
    status: 'Active',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-04-20T13:30:00Z',
    updatedAt: '2023-04-20T13:30:00Z',
    lastPublishedAt: '2023-04-20T13:30:00Z',
    isDefault: false
  }, {
    id: 'wf-004',
    name: 'Mexico PO Approval',
    description: 'Purchase order approval workflow for Mexico operations',
    documentType: 'po',
    version: '1.1',
    status: 'Active',
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-03-01T10:20:00Z',
    updatedAt: '2023-05-12T09:45:00Z',
    lastPublishedAt: '2023-05-12T09:45:00Z',
    isDefault: false
  }]);
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Handler for creating a new workflow
  const handleCreateWorkflow = () => {
    const newWorkflow: WorkflowDefinition = {
      id: `wf-${new Date().getTime()}`,
      name: 'New Workflow',
      description: '',
      documentType: '',
      version: '0.1',
      status: 'Draft',
      scope: 'Global',
      bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDefault: false
    };
    setSelectedWorkflow(newWorkflow);
    setActiveView('edit');
  };
  // Handler for editing a workflow
  const handleEditWorkflow = (workflow: WorkflowDefinition) => {
    setSelectedWorkflow(workflow);
    setActiveView('edit');
  };
  // Handler for previewing a workflow
  const handlePreviewWorkflow = (workflow: WorkflowDefinition) => {
    setSelectedWorkflow(workflow);
    setActiveView('preview');
    setZoomLevel(100); // Reset zoom level
  };
  // Handler for duplicating a workflow
  const handleDuplicateWorkflow = (workflow: WorkflowDefinition) => {
    const duplicatedWorkflow: WorkflowDefinition = {
      ...workflow,
      id: `wf-${new Date().getTime()}`,
      name: `Copy of ${workflow.name}`,
      status: 'Draft',
      version: '0.1',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lastPublishedAt: undefined,
      isDefault: false
    };
    setWorkflows([...workflows, duplicatedWorkflow]);
  };
  // Handler for activating/deactivating a workflow
  const handleToggleWorkflowStatus = (workflowId: string) => {
    setWorkflows(workflows.map(wf => wf.id === workflowId ? {
      ...wf,
      status: wf.status === 'Active' ? 'Inactive' : wf.status === 'Inactive' ? 'Active' : wf.status,
      updatedAt: new Date().toISOString(),
      ...(wf.status === 'Inactive' ? {
        lastPublishedAt: new Date().toISOString()
      } : {})
    } : wf));
  };
  // Handler for publishing a draft workflow
  const handlePublishWorkflow = (workflowId: string) => {
    setWorkflows(workflows.map(wf => wf.id === workflowId ? {
      ...wf,
      status: 'Active',
      updatedAt: new Date().toISOString(),
      lastPublishedAt: new Date().toISOString()
    } : wf));
  };
  // Handler for deleting a workflow
  const handleDeleteWorkflow = (workflowId: string) => {
    setDeletingId(workflowId);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setWorkflows(workflows.filter(wf => wf.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  // Handler for setting a workflow as default for its document type
  const handleSetAsDefault = (workflow: WorkflowDefinition) => {
    setWorkflows(workflows.map(wf => wf.documentType === workflow.documentType ? {
      ...wf,
      isDefault: wf.id === workflow.id,
      updatedAt: wf.id === workflow.id ? new Date().toISOString() : wf.updatedAt
    } : wf));
  };
  // Handler for saving a workflow
  const handleSaveWorkflow = (updatedWorkflow: WorkflowDefinition) => {
    if (updatedWorkflow.id && workflows.find(w => w.id === updatedWorkflow.id)) {
      // Update existing workflow
      setWorkflows(workflows.map(wf => wf.id === updatedWorkflow.id ? {
        ...updatedWorkflow,
        updatedAt: new Date().toISOString()
      } : wf));
    } else {
      // Create new workflow
      const newWorkflow = {
        ...updatedWorkflow,
        id: updatedWorkflow.id || `wf-${new Date().getTime()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setWorkflows([...workflows, newWorkflow]);
    }
    setActiveView('list');
    setSelectedWorkflow(null);
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    if (selectedWorkflow) {
      setSelectedWorkflow({
        ...selectedWorkflow,
        orgUnitId: orgUnitId,
        orgUnitName: orgUnit?.name || '',
        orgUnitPath: orgUnit?.path || ''
      });
    }
    setSelectedOrgUnit(orgUnit);
  };
  // Filter workflows based on search term and document type filter
  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) || workflow.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDocumentType = filterDocumentType === 'all' || workflow.documentType === filterDocumentType;
    return matchesSearch && matchesDocumentType;
  });
  // Get document type name from ID
  const getDocumentTypeName = (id: string) => {
    const docType = documentTypes.find(dt => dt.id === id);
    return docType ? docType.name : id;
  };
  const globalWorkflows = filteredWorkflows.filter(w => w.scope === 'Global');
  const orgUnitWorkflows = filteredWorkflows.filter(w => w.scope === 'OrgUnit');
  // Render the workflows list
  const renderWorkflowsList = () => {
    return <div className="space-y-4" data-id="element-12907">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-4 sm:space-y-0" data-id="element-12908">
          <div data-id="element-12909">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-12910">
              Workflow Definitions
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-12911">
              Manage business process workflows at global and organisation unit
              levels
            </p>
          </div>
          <button onClick={handleCreateWorkflow} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12912">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-12913" />
            Create Workflow
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-4" data-id="element-12914">
          <div className="relative rounded-md shadow-sm flex-1" data-id="element-12915">
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-3 pr-10 py-2 sm:text-sm border-gray-300 rounded-md" placeholder="Search workflows..." data-id="element-12916" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-12917">
              <FileTextIcon className="h-4 w-4 text-gray-400" data-id="element-12918" />
            </div>
          </div>
          <div className="sm:w-64" data-id="element-12919">
            <select value={filterDocumentType} onChange={e => setFilterDocumentType(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-12920">
              <option value="all" data-id="element-12921">All Document Types</option>
              {documentTypes.map(docType => <option key={docType.id} value={docType.id} data-id="element-12922">
                  {docType.name}
                </option>)}
            </select>
          </div>
        </div>

        {/* Global Workflows */}
        <div className="bg-white shadow rounded-lg mb-6" data-id="element-12923">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12924">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12925">
              Global Workflows
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-12926">
              Global workflows available to all organisation units
            </p>
          </div>

          {globalWorkflows.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-12927">
              <p className="text-sm text-gray-500" data-id="element-12928">
                No global workflows found.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-12929">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-12930">
                <thead className="bg-gray-50" data-id="element-12931">
                  <tr data-id="element-12932">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12933">
                      Workflow
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12934">
                      Document Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12935">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12936">
                      Version
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12937">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-12938">
                  {globalWorkflows.map(workflow => <tr key={workflow.id} data-id="element-12939">
                      <td className="px-6 py-4" data-id="element-12940">
                        <div className="text-sm font-medium text-gray-900 flex items-center" data-id="element-12941">
                          {workflow.name}
                          {workflow.isDefault && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-12942">
                              Default
                            </span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-12943">
                          {workflow.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-12944">
                        {getDocumentTypeName(workflow.documentType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-12945">
                        {workflow.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-12946" />}
                        {workflow.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-12947" />}
                        {workflow.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-12948" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-12949">
                        v{workflow.version}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12950">
                        <div className="flex justify-end space-x-2" data-id="element-12951">
                          <button onClick={() => handleEditWorkflow(workflow)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-12952">
                            <EditIcon className="h-4 w-4" data-id="element-12953" />
                          </button>
                          <button onClick={() => handlePreviewWorkflow(workflow)} className="text-blue-600 hover:text-blue-900" title="Preview" data-id="element-12954">
                            <PlayIcon className="h-4 w-4" data-id="element-12955" />
                          </button>
                          <button onClick={() => handleDuplicateWorkflow(workflow)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-12956">
                            <CopyIcon className="h-4 w-4" data-id="element-12957" />
                          </button>
                          {workflow.status === 'Draft' && <button onClick={() => handlePublishWorkflow(workflow.id)} className="text-green-600 hover:text-green-900" title="Publish" data-id="element-12958">
                              <CheckCircleIcon className="h-4 w-4" data-id="element-12959" />
                            </button>}
                          <button onClick={() => handleDeleteWorkflow(workflow.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-12960">
                            <TrashIcon className="h-4 w-4" data-id="element-12961" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>

        {/* OrgUnit Workflows */}
        <div className="bg-white shadow rounded-lg" data-id="element-12962">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12963">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12964">
              Organisation Unit Workflows
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-12965">
              Workflows specific to individual organisation units
            </p>
          </div>

          {orgUnitWorkflows.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-12966">
              <p className="text-sm text-gray-500" data-id="element-12967">
                No organisation unit workflows found.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-12968">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-12969">
                <thead className="bg-gray-50" data-id="element-12970">
                  <tr data-id="element-12971">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12972">
                      Organisation Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12973">
                      Workflow
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12974">
                      Document Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12975">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12976">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-12977">
                  {orgUnitWorkflows.map(workflow => <tr key={workflow.id} data-id="element-12978">
                      <td className="px-6 py-4" data-id="element-12979">
                        <div className="text-sm font-medium text-gray-900" data-id="element-12980">
                          {workflow.orgUnitName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-12981">
                          {workflow.orgUnitPath}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-12982">
                        <div className="text-sm font-medium text-gray-900" data-id="element-12983">
                          {workflow.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-12984">
                          {workflow.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-12985">
                        {getDocumentTypeName(workflow.documentType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-12986">
                        {workflow.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-12987" />}
                        {workflow.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-12988" />}
                        {workflow.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-12989" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12990">
                        <div className="flex justify-end space-x-2" data-id="element-12991">
                          <button onClick={() => handleEditWorkflow(workflow)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-12992">
                            <EditIcon className="h-4 w-4" data-id="element-12993" />
                          </button>
                          <button onClick={() => handlePreviewWorkflow(workflow)} className="text-blue-600 hover:text-blue-900" title="Preview" data-id="element-12994">
                            <PlayIcon className="h-4 w-4" data-id="element-12995" />
                          </button>
                          <button onClick={() => handleDuplicateWorkflow(workflow)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-12996">
                            <CopyIcon className="h-4 w-4" data-id="element-12997" />
                          </button>
                          {workflow.status === 'Draft' && <button onClick={() => handlePublishWorkflow(workflow.id)} className="text-green-600 hover:text-green-900" title="Publish" data-id="element-12998">
                              <CheckCircleIcon className="h-4 w-4" data-id="element-12999" />
                            </button>}
                          <button onClick={() => handleDeleteWorkflow(workflow.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-13000">
                            <TrashIcon className="h-4 w-4" data-id="element-13001" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>;
  };
  // Render the workflow editor (simplified - keeping existing complex logic)
  const renderWorkflowEditor = () => {
    if (!selectedWorkflow) return null;
    return <div className="space-y-6" data-id="element-13002">
        <div className="flex justify-between items-center mb-4" data-id="element-13003">
          <button onClick={() => {
          setActiveView('list');
          setSelectedWorkflow(null);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13004">
            ← Back to Workflows
          </button>
          <div className="flex space-x-2" data-id="element-13005">
            <button onClick={() => handleSaveWorkflow(selectedWorkflow)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13006">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-13007" />
              Save
            </button>
          </div>
        </div>

        {/* Workflow details form */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13008">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-13009">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13010">
              Workflow Details
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-13011">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-13012">
              {/* Scope */}
              <div className="sm:col-span-3" data-id="element-13013">
                <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-13014">
                  Scope <span className="text-red-500" data-id="element-13015">*</span>
                </label>
                <div className="mt-1" data-id="element-13016">
                  <select id="scope" value={selectedWorkflow.scope} onChange={e => {
                  const newScope = e.target.value as 'Global' | 'OrgUnit';
                  setSelectedWorkflow({
                    ...selectedWorkflow,
                    scope: newScope,
                    ...(newScope === 'Global' ? {
                      orgUnitId: undefined,
                      orgUnitName: undefined,
                      orgUnitPath: undefined
                    } : {})
                  });
                }} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13017">
                    <option value="Global" data-id="element-13018">Global</option>
                    <option value="OrgUnit" data-id="element-13019">Organisation Unit</option>
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-13020">
                  {selectedWorkflow.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
                </p>
              </div>

              {/* OrgUnit Selector */}
              {selectedWorkflow.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-13021">
                  <OrgUnitSelector value={selectedWorkflow.orgUnitId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} data-id="element-13022" />
                </div>}

              <div className="sm:col-span-3" data-id="element-13023">
                <label htmlFor="workflow-name" className="block text-sm font-medium text-gray-700" data-id="element-13024">
                  Name
                </label>
                <div className="mt-1" data-id="element-13025">
                  <input type="text" id="workflow-name" value={selectedWorkflow.name} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  name: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13026" />
                </div>
              </div>

              <div className="sm:col-span-3" data-id="element-13027">
                <label htmlFor="document-type" className="block text-sm font-medium text-gray-700" data-id="element-13028">
                  Document Type
                </label>
                <div className="mt-1" data-id="element-13029">
                  <select id="document-type" value={selectedWorkflow.documentType} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  documentType: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13030">
                    <option value="" data-id="element-13031">Select Document Type</option>
                    {documentTypes.map(docType => <option key={docType.id} value={docType.id} data-id="element-13032">
                        {docType.name}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="sm:col-span-6" data-id="element-13033">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-13034">
                  Description
                </label>
                <div className="mt-1" data-id="element-13035">
                  <textarea id="description" rows={3} value={selectedWorkflow.description} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  description: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13036" />
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-13037">
                <label htmlFor="version" className="block text-sm font-medium text-gray-700" data-id="element-13038">
                  Version
                </label>
                <div className="mt-1" data-id="element-13039">
                  <input type="text" id="version" value={selectedWorkflow.version} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  version: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13040" />
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-13041">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-13042">
                  Status
                </label>
                <div className="mt-1" data-id="element-13043">
                  <select id="status" value={selectedWorkflow.status} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  status: e.target.value as 'Active' | 'Draft' | 'Inactive'
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13044">
                    <option value="Draft" data-id="element-13045">Draft</option>
                    <option value="Active" data-id="element-13046">Active</option>
                    <option value="Inactive" data-id="element-13047">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-13048">
                <label htmlFor="default" className="block text-sm font-medium text-gray-700" data-id="element-13049">
                  Default Workflow
                </label>
                <div className="mt-1" data-id="element-13050">
                  <select id="default" value={selectedWorkflow.isDefault ? 'yes' : 'no'} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  isDefault: e.target.value === 'yes'
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-13051">
                    <option value="no" data-id="element-13052">No</option>
                    <option value="yes" data-id="element-13053">Yes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BPMN Diagram Editor Placeholder */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13054">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-13055">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13056">
              Workflow Diagram
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-13057">
            <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg text-center" data-id="element-13058">
              <LayersIcon className="h-12 w-12 text-gray-400" data-id="element-13059" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-13060">
                BPMN Workflow Editor
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-13061">
                The BPMN editor would be integrated here to visually design the
                workflow
              </p>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render preview (keeping simplified version)
  const renderWorkflowPreview = () => {
    if (!selectedWorkflow) return null;
    return <div className="space-y-6" data-id="element-13062">
        <div className="flex justify-between items-center mb-4" data-id="element-13063">
          <button onClick={() => {
          setActiveView('list');
          setSelectedWorkflow(null);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-13064">
            ← Back to Workflows
          </button>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13065">
          <div className="px-4 py-5 sm:px-6" data-id="element-13066">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13067">
              {selectedWorkflow.name}
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-13068">
              {selectedWorkflow.description}
            </p>
          </div>
        </div>
      </div>;
  };
  // Main render function
  return <div className="space-y-6" data-id="element-13069">
      <div className="mb-8" data-id="element-13070">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-13071">Workflows</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-13072">
          Design and manage business process workflows with BPMN visualization
        </p>
      </div>

      {/* Main Content */}
      {activeView === 'list' ? renderWorkflowsList() : activeView === 'edit' ? renderWorkflowEditor() : renderWorkflowPreview()}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-13073">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-13074">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-13075">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-13076"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-13077">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-13078">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-13079">
                <div className="sm:flex sm:items-start" data-id="element-13080">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-13081">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-13082" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-13083">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13084">
                      Delete Workflow
                    </h3>
                    <div className="mt-2" data-id="element-13085">
                      <p className="text-sm text-gray-500" data-id="element-13086">
                        Are you sure you want to delete this workflow? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-13087">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-13088">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-13089">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}